import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  Box,
  Divider,
  Grid,
  GridItem,
  IconButton,
  Image,
  Stack,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { InfoCard } from '@/components/InfoCard';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { Card } from '@/imports/ui/chakra/components/Card';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { EditIcon } from '@/imports/ui/chakra/feather';
import { Butterfly, ButterflyMain } from '@/layouts/Butterfly';

import {
  Company_Organization,
  Company_OrganizationVariables,
} from './graphql-types/Company_Organization';

const ORGANIZATION = gql`
  query Company_Organization(
    $singleOrganizationInput: SingleOrganizationInput!
  ) {
    organization(input: $singleOrganizationInput) {
      result {
        _id
        currentUserOrganizationPermission {
          canManageOrganizationProfile
        }
        displayName
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
        aboutUs
        ...OrganizationProfileImage_OrganizationFragment
        ...OrganizationPageInformation_OrganizationFragment
      }
    }
  }
  ${OrganizationProfileImage.organizationFragment}
  ${InfoCard.organizationFragment}
`;

export const Company: React.FC = () => {
  const router = useRouter();

  const id = router.query.organizationId.toString();

  const query = useQuery<Company_Organization, Company_OrganizationVariables>(
    ORGANIZATION,
    {
      variables: {
        singleOrganizationInput: {
          id,
        },
      },
    }
  );

  const organization = query.data?.organization?.result;

  return (
    <Butterfly>
      <ButterflyMain>
        <Box maxW="container.xl" mx="auto" p="4">
          <Grid templateColumns="repeat(12, 1fr)" gap="4">
            <GridItem
              colSpan={{ base: 12, lg: 3 }}
              display={{ base: 'none', lg: 'block' }}
            >
              {/* empty */}
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 8, lg: 6 }}>
              {query.networkStatus === NetworkStatus.loading ? (
                <Loading />
              ) : query.error ? (
                <UnexpectedError />
              ) : !organization ? (
                <UnexpectedError />
              ) : (
                <Stack spacing="4">
                  <Card position="relative">
                    {organization.bannerImage?.imageProxy ? (
                      <Image
                        alt="company banner"
                        borderTopRadius="xl"
                        src={organization.bannerImage.imageProxy}
                      />
                    ) : (
                      <ImageFallback borderTopRadius="xl" aspectRatio="8/3" />
                    )}

                    {organization.currentUserOrganizationPermission
                      .canManageOrganizationProfile && (
                      <Box position="absolute" top="4" right="4">
                        <NextLink href={`/my-company/dashboard/overview`}>
                          <IconButton
                            icon={<EditIcon />}
                            aria-label="edit profile"
                            variant="outline"
                            colorScheme="gray"
                            size="sm"
                          />
                        </NextLink>
                      </Box>
                    )}

                    <Stack spacing="0" divider={<Divider />}>
                      <Stack
                        direction={{ base: 'column', md: 'row' }}
                        alignItems={{ base: 'center', md: 'flex-start' }}
                        spacing={{ base: 2, md: 4 }}
                        p="4"
                      >
                        <OrganizationProfileImage
                          mt={{ base: -16, md: -10 }}
                          h={{ base: 28, md: 36 }}
                          w={{ base: 28, md: 36 }}
                          organization={organization}
                        />
                        <Text
                          textStyle="xlBodyStrong"
                          textAlign={{ base: 'center', md: 'left' }}
                        >
                          {organization.displayName}
                        </Text>
                      </Stack>
                    </Stack>
                  </Card>
                </Stack>
              )}
            </GridItem>
            <GridItem
              colSpan={{ base: 12, md: 4, lg: 3 }}
              display={{ base: 'none', md: 'block' }}
            >
              {organization && <InfoCard organization={organization} />}
            </GridItem>
          </Grid>
        </Box>
      </ButterflyMain>
    </Butterfly>
  );
};
